import { ActionIcon, ScrollArea, Skeleton, Text, UnstyledButton } from "@mantine/core";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Check, Pause, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  getListVoicesQueryKey,
  listVoices,
} from "../../api/generated/endpoints/generation/generation";
import type { ListVoicesParams, Voice } from "../../api/generated/model";
import { playsAlone, stoppedPlaying } from "../../ui/AudioPlayer";
import classes from "./voices.module.css";

/** As many as the route gives at once: live lists every voice in one go. */
const PAGE = 100;

export interface VoiceListProps {
  teamId: string;
  brandId: string;
  /** Which voices and live's filters, as the route takes them. */
  params: ListVoicesParams;
  /** The voice the form holds, marked and scrolled to. */
  chosen: string | null;
  onChoose: (voice: Voice) => void;
  /** Live's words with none: "No global voices found", "No custom voices found". */
  empty: string;
  /** Why this form cannot use a voice, shown on its disabled row, or null. */
  whyNot?: ((voice: Voice) => string | null) | undefined;
}

/**
 * Every voice the route lists for `params`, read a page after another as live's list shows
 * them all. The list and the Talking Head's actors (their own voices) share these pages.
 */
export function useVoicePages(
  teamId: string,
  brandId: string,
  params: ListVoicesParams,
  /** Off until the form needs them (a clone's voices' names). */
  enabled = true,
) {
  const voices = useInfiniteQuery({
    enabled,
    queryKey: [...getListVoicesQueryKey(teamId, brandId, params), "pages"],
    queryFn: ({ pageParam, signal }) =>
      listVoices(
        teamId,
        brandId,
        { ...params, limit: PAGE, ...(pageParam === undefined ? {} : { after: pageParam }) },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = voices;
  // Live shows them all: the next page comes as soon as one arrives.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  return voices;
}

/**
 * Live's voice list (`voices-list`, captures 03 and L02): every voice, each a row with its play
 * button and its name, the chosen one with live's border and check, scrolled to the middle. A
 * row's name chooses it; its play button only plays, one sample at a time. Five grey rows
 * while it loads; live's words with none.
 */
export function VoiceList(props: VoiceListProps) {
  const { teamId, brandId, params, chosen, onChoose, empty, whyNot } = props;
  const voices = useVoicePages(teamId, brandId, params);
  const items = voices.data?.pages.flatMap((page) => page.items) ?? [];
  if (voices.isPending) {
    return (
      <div className={classes.rows} aria-label="Loading the voices">
        {Array.from({ length: 5 }, (_, at) => (
          <div key={at} className={classes.row}>
            <Skeleton circle height={30} />
            <Skeleton height={12} radius="xl" />
          </div>
        ))}
      </div>
    );
  }
  // Live shows its empty words when the list fails too.
  return (
    <VoiceRows
      voices={items}
      chosen={chosen}
      onChoose={onChoose}
      empty={empty}
      whyNot={whyNot}
      onLost={() => void voices.refetch()}
    />
  );
}

export interface VoiceRowsProps {
  voices: Voice[];
  /** The voice the form holds, marked and scrolled to. */
  chosen: string | null;
  onChoose: (voice: Voice) => void;
  /** Live's words with none. */
  empty: string;
  /** A sample's signed link no longer loads: the voices are read again. */
  onLost: () => void;
  /** Why this form cannot use a voice, shown on its disabled row, or null. */
  whyNot?: ((voice: Voice) => string | null) | undefined;
}

/**
 * Live's voice rows (`voices-list`): each its play button and its name, the chosen one with
 * live's border and check, scrolled to the list's middle; live's words with none.
 */
export function VoiceRows({ voices, chosen, onChoose, empty, onLost, whyNot }: VoiceRowsProps) {
  const chosenRow = useRef<HTMLDivElement>(null);
  const viewport = useRef<HTMLDivElement>(null);
  // The chosen row to the list's middle. Only the list moves, as live's: `scrollIntoView` would
  // scroll the page behind the panel as well.
  useEffect(() => {
    const row = chosenRow.current;
    const list = viewport.current;
    if (row === null || list === null) return;
    const offset = row.getBoundingClientRect().top - list.getBoundingClientRect().top;
    const top = list.scrollTop + offset - (list.clientHeight - row.offsetHeight) / 2;
    list.scrollTo?.({ top, behavior: "smooth" });
  }, [chosen, voices.length]);
  if (voices.length === 0) return <Text className={classes.empty}>{empty}</Text>;
  return (
    <ScrollArea type="scroll" scrollbarSize={6} className={classes.list} viewportRef={viewport}>
      <div className={classes.rows}>
        {voices.map((voice) => {
          const held = voice.id === chosen;
          const why = whyNot?.(voice) ?? null;
          return (
            <div
              key={voice.id}
              ref={voice.id === chosen ? chosenRow : undefined}
              className={classes.row}
              data-chosen={held || undefined}
              data-why={why !== null || undefined}
            >
              <VoicePlay url={voice.preview_url} name={voice.name} onLost={onLost} />
              <UnstyledButton
                className={classes.choose}
                aria-pressed={held}
                disabled={why !== null}
                onClick={() => onChoose(voice)}
              >
                <span className={classes.name}>{voice.name}</span>
                {voice.id === chosen ? (
                  <Check size={20} className={classes.check} aria-hidden />
                ) : null}
              </UnstyledButton>
              {why === null ? null : <Text className={classes.why}>{why}</Text>}
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}

interface VoicePlayProps {
  url: string;
  name: string;
  /** The signed link no longer loads: the list is read again for a fresh one. */
  onLost: () => void;
}

/** A voice's sample: live's round play button, one sample at a time on the page. */
export function VoicePlay({ url, name, onLost }: VoicePlayProps) {
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    const element = audio.current;
    return () => element?.pause();
  }, []);
  return (
    <>
      <ActionIcon
        variant="light"
        size={32}
        radius="xl"
        aria-label={playing ? `Pause ${name}` : `Play ${name}`}
        onClick={() => {
          const element = audio.current;
          if (element === null) return;
          if (element.paused) void element.play().catch(() => setPlaying(false));
          else element.pause();
        }}
      >
        {playing ? <Pause size={16} /> : <Play size={16} />}
      </ActionIcon>
      {/* A voice sample has no captions to give. */}
      {/* oxlint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audio}
        src={url}
        preload="none"
        onPlay={(event) => {
          playsAlone(event.currentTarget);
          setPlaying(true);
        }}
        onPause={(event) => {
          stoppedPlaying(event.currentTarget);
          setPlaying(false);
        }}
        onEnded={(event) => {
          event.currentTarget.currentTime = 0;
        }}
        onError={onLost}
      />
    </>
  );
}
